/**
 * The subtitle keys while the Subtitles tab (asset) or side tab (editor) is open (1.9.0). Listened to on the window
 * before the page's own shortcuts (capture), matched on `e.code` (keys.ts), so the Mac's Option dead keys (⌥N, ⌥I)
 * do not type a character in the cue text and a key that acts is not also seen by the Player or the editor. ⌘I / ⌘B
 * are the cue text area's own (CueRow). `handle` returns false when the key had nothing to act on: the press then
 * goes on as usual (the editor's slip keys).
 */
import { useEffect, useRef } from 'react';
import { IS_MAC, subtitleKeyAction, type SubtitleKeyAction } from './keys';

export function useSubtitleKeys(enabled: boolean, handle: (action: SubtitleKeyAction, e: KeyboardEvent) => boolean) {
  const latest = useRef(handle);
  latest.current = handle;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      const t = e.target as Element | null;
      if (t?.closest?.('select, .modal, [role=dialog], [role=menu]')) return;
      const a = subtitleKeyAction(e, IS_MAC);
      if (!a || a.action === 'italic' || a.action === 'bold') return;
      if (!latest.current(a, e)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [enabled]);
}

/** The caret in a cue text area that has focus (data-cue-text = its cue's id), for ⌥S. */
export function focusedCueCaret(id: string | undefined): number | null {
  const el = document.activeElement;
  if (!id || !(el instanceof HTMLTextAreaElement) || el.dataset.cueText !== id) return null;
  return el.selectionStart;
}

/** Puts the caret in a cue's text area (after ⌥N), once it is drawn. */
export function focusCueText(id: string) {
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLTextAreaElement>(`textarea[data-cue-text="${CSS.escape(id)}"]`);
    if (!el) return;
    el.focus();
    el.scrollIntoView({ block: 'nearest' });
  });
}
